/**
 * Company Approvals — SystemAdmin, or a user granted CanManageClients.
 * Self-service signups are created inactive (AutoActivateUsers=false). This screen
 * lists companies awaiting review and lets an admin Approve (activate + email them)
 * or Reject (drop from the queue).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2, RefreshCw, AlertCircle, CheckCircle, XCircle, Mail, MapPin, Phone, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import ConfirmDialog, { useConfirm } from '../../components/ConfirmDialog';
import Footer from '../../components/Footer';
import SetupActivateModal from '../../components/admin/SetupActivateModal';
import { getErrorMessage } from '../../services/apiService';
import { listPendingCompanies, rejectCompany, PendingCompanyResponse } from '../../services/clientsAdminService';
import { formatPhone } from '../../utils/validation';

const ClientApprovals: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = user?.isSystemAdmin === true;
  const canManageClients = isSystemAdmin || user?.canManageClients === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!canManageClients) navigate('/dashboard');
  }, [user, authLoading, navigate, canManageClients]);

  const [rows, setRows] = useState<PendingCompanyResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();
  const [activating, setActivating] = useState<PendingCompanyResponse | null>(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      setRows(await listPendingCompanies());
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { if (canManageClients) load(); }, [canManageClients, load]);

  const handleActivated = (name: string) => {
    const id = activating?.clientId;
    setActivating(null);
    if (id) setRows((r) => r.filter((x) => x.clientId !== id));
    setNotice(`${name} is set up and activated — invites have been emailed.`);
  };

  const handleReject = (c: PendingCompanyResponse) => {
    confirm({
      title: 'Reject this company?',
      message: (
        <>
          <span className="font-semibold text-[#0f172a]">{c.companyName}</span> is removed from the
          approval queue. They are not notified from here.
        </>
      ),
      confirmLabel: 'Reject',
      tone: 'danger',
      action: () => doReject(c),
    });
  };

  const doReject = async (c: PendingCompanyResponse) => {
    setBusyId(c.clientId); setError(''); setNotice('');
    try {
      await rejectCompany(c.clientId);
      setRows((r) => r.filter((x) => x.clientId !== c.clientId));
      setNotice(`Rejected ${c.companyName}.`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 lg:ml-[220px] pt-28 md:pt-32 px-4 lg:px-8 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-[#0f172a] flex items-center gap-3">
                <ShieldCheck className="w-7 h-7 text-[#c41e3a]" />
                Company Approvals
                {rows.length > 0 && (
                  <span className="text-sm font-semibold bg-[#c41e3a] text-white rounded-full px-2.5 py-0.5">{rows.length}</span>
                )}
              </h1>
              <p className="text-[#64748b] mt-1">Access requests awaiting review. "Onboard" completes the org info + banking, invites its users, and activates the account.</p>
            </div>
            <button
              onClick={load}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-black/[0.08] bg-white text-[#475569] hover:border-[#c41e3a]/50 transition-all self-start"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {notice && (
            <div className="mb-6 p-4 rounded-xl border flex items-start space-x-3 bg-green-50 border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{notice}</p>
            </div>
          )}
          {error && (
            <div className="mb-6 p-4 rounded-xl border flex items-start space-x-3 bg-red-50 border-red-200">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="bg-white rounded-2xl border border-black/[0.08] p-12 text-center text-[#64748b]">
              <Loader2 className="w-6 h-6 animate-spin inline mr-2" />Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-2xl border border-black/[0.08] p-12 text-center">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="text-[#475569] font-medium">All caught up</p>
              <p className="text-[#94a3b8] text-sm mt-1">No companies are awaiting approval.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((c) => (
                <div key={c.clientId} className="bg-white rounded-2xl border border-black/[0.08] p-6 shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    <div className="md:col-span-5">
                      <h3 className="font-bold text-[#0f172a] text-lg">{c.companyName}</h3>
                      {c.description && <p className="text-sm text-[#64748b] mt-1">{c.description}</p>}
                      <p className="text-xs text-[#94a3b8] mt-2 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        Applied {new Date(c.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    </div>

                    <div className="md:col-span-4 text-sm space-y-1.5">
                      {c.contactName && <p className="text-[#0f172a] font-medium">{c.contactName}</p>}
                      {c.contactEmail && (
                        <p className="text-[#64748b] flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{c.contactEmail}</p>
                      )}
                      {(c.city || c.state) && (
                        <p className="text-[#64748b] flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{[c.city, c.state, c.zipCode].filter(Boolean).join(', ')}</p>
                      )}
                      {c.phone && <p className="text-[#64748b] flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{formatPhone(c.phone)}</p>}
                    </div>

                    <div className="md:col-span-3 flex md:flex-col gap-2 md:justify-center">
                      <button
                        onClick={() => { setError(''); setNotice(''); setActivating(c); }}
                        disabled={busyId === c.clientId}
                        className="flex-1 px-4 py-2 rounded-lg bg-[#c41e3a] hover:bg-[#a01830] text-white font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <ShieldCheck className="w-4 h-4" />
                        Onboard
                      </button>
                      <button
                        onClick={() => handleReject(c)}
                        disabled={busyId === c.clientId}
                        className="flex-1 px-4 py-2 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {busyId === c.clientId ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      {activating && (
        <SetupActivateModal
          company={activating}
          onClose={() => setActivating(null)}
          onActivated={handleActivated}
        />
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default ClientApprovals;
